import PropTypes from 'prop-types'
import dynamic from 'next/dynamic'
import { Container, H2, H3, MainLink, P1, P4, Rating } from '@moneymade/moneymade-ui'
import Link from 'next/link'
import { useState, useMemo } from 'react'

import { ROUTES } from 'static/menusConstants'
import { getCdnImg } from 'utils/getters'
import { toConvertMoney } from 'modules/learn/learnUtils'
import styles from './TopBlock.module.scss'

const ModalPreview = dynamic(() => import('modules/discover/PlatformPage/PlatformInfo/ModalPreview/ModalPreview'))
const MediaSlider = dynamic(() => import('modules/discover/PlatformPageNew/TopBlock/MediaSlider/MediaSlider'))

const TopBlock = ({
  name,
  logo,
  verificationStatus,
  investorsTotal,
  promoVideo,
  brandVideo,
  media,
  rating,
  joinInvestorsText,
  industry,
  backgroundColor,
  collapsedDescription,
  totalReviews,
  assetPageSlug,
  topBlock,
  website
}) => {

  const [curentSlideIndex, setCurentSlideIndex] = useState(0)
  const [swiper, setSwiper] = useState(null)
  const [isModal, setModal] = useState(false)

  // Slider videos
  const videos = useMemo(
    () => [...(promoVideo ? [promoVideo] : []), ...(brandVideo ? [brandVideo] : [])],
    [promoVideo, brandVideo]
  )
  const slidesLength = useMemo(() => [...videos, ...(media || [])].length - 1 || 0, [media, videos])

  // Availavility for the nav buttons
  const isPrevActive = curentSlideIndex === 0
  const isNextActive = curentSlideIndex === slidesLength

  return (
    <>
      {isModal && (
        <ModalPreview
          videos={videos}
          media={media}
          isModal={isModal}
          index={curentSlideIndex}
          actions={{
            isPrevActive,
            isNextActive,
            onPrev: swiper?.navigation?.onPrevClick,
            onNext: swiper?.navigation?.onNextClick
          }}
          handleSetModal={setModal}
        />
      )}

      <div className={styles.TopBlock} ref={topBlock} style={{ backgroundColor: backgroundColor || '#000' }}>
        <Container width={1230} className={styles.Content}>
          <div className={styles.Left}>
            <div className={styles.Header}>
              <img src={getCdnImg(logo, 92)} alt="logo" width={92} height={92} />

              <div className={styles.Title}>
                <H2 weight="semibold" type="main">
                  {name}

                  <div className={styles.Icons}>
                    {verificationStatus === 'Verified Paying (Premium)' && (
                      <>
                        <svg
                          width="24"
                          height="24"
                          fill="#61DBFB"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 512 512"
                        >
                          <path d="M256 0c36.8 0 68.8 20.7 84.9 51.1C373.8 41 411 49 437 75s34 63.3 23.9 96.1C491.3 187.2 512 219.2 512 256s-20.7 68.8-51.1 84.9C471 373.8 463 411 437 437s-63.3 34-96.1 23.9C324.8 491.3 292.8 512 256 512s-68.8-20.7-84.9-51.1C138.2 471 101 463 75 437s-34-63.3-23.9-96.1C20.7 324.8 0 292.8 0 256s20.7-68.8 51.1-84.9C41 138.2 49 101 75 75s63.3-34 96.1-23.9C187.2 20.7 219.2 0 256 0zM369 209c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-111 111-47-47c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l64 64c9.4 9.4 24.6 9.4 33.9 0L369 209z" />
                        </svg>
                        <P4 className={styles.IconVerified} weight="semibold">
                          Verified
                        </P4>
                      </>
                    )}
                  </div>
                </H2>
                <div className={styles.Rating}>
                  {rating ? <Rating rating={rating} color="red" layout="single" withNumber /> : null}
                  {totalReviews && (
                    <P1>
                      ({totalReviews} {totalReviews === 1 ? 'Review' : 'Reviews'})
                    </P1>
                  )}
                  <span className={styles.Dot}>•</span>

                  {!assetPageSlug ? (
                    <P1 weight="normal" className={styles.Industry}>
                      {industry?.name}
                    </P1>
                  ) : (
                    <Link href={`/${ROUTES.assetPage}/${industry?.assetSlug}`} aria-hidden prefetch={false}>
                      <a className={`${styles.Industry} ${styles.Hoverable}`}>
                        <P1 weight="normal">{industry?.name}</P1>
                      </a>
                    </Link>
                  )}
                </div>
              </div>
            </div>

            <H3
              nestedStyles
              type="main"
              className={styles.Description}
              dangerouslySetInnerHTML={{ __html: collapsedDescription }}
            />

            <div className={styles.Actions}>
              <MainLink
                href={website}
                target="_blank"
                rel="noreferrer"
                fontType="main"
                weight="light"
                size="md"
              >
                {investorsTotal
                  ? `Join ${toConvertMoney(investorsTotal)} investors`
                  : joinInvestorsText || 'Join investors'}
              </MainLink>
            </div>
          </div>
          <div className={styles.Right}>
            {(!!videos?.length || !!media?.length) && (
              <div className={styles.RightSide}>
                  <MediaSlider
                    swiper={swiper}
                    videos={videos}
                    media={media}
                    isActive={{ prev: isPrevActive, next: isNextActive }}
                    handleOpenModal={() => setModal(true)}
                    handleSetSwiper={setSwiper}
                    handleSetIndex={setCurentSlideIndex}
                  />
              </div>
            )}
          </div>
        </Container>
      </div>
    </>
  )
}

TopBlock.propTypes = {
  name: PropTypes.string,
  website: PropTypes.string,
  logo: PropTypes.string,
  verificationStatus: PropTypes.string,
  rating: PropTypes.number,
  joinInvestorsText: PropTypes.string,
  investorsTotal: PropTypes.number,
  promoVideo: PropTypes.string,
  brandVideo: PropTypes.string,
  media: PropTypes.arrayOf(PropTypes.string),
  industry: PropTypes.shape({
    name: PropTypes.string,
    website: PropTypes.string,
    assetSlug: PropTypes.string
  }),
  backgroundColor: PropTypes.string,
  collapsedDescription: PropTypes.string,
  totalReviews: PropTypes.number,
  assetPageSlug: PropTypes.string,
  topBlock: PropTypes.shape({
    params: PropTypes.node
  })
}

TopBlock.defaultProps = {
  name: '',
  logo: '',
  website: '',
  verificationStatus: '',
  joinInvestorsText: '',
  investorsTotal: 0,
  rating: 0,
  promoVideo: '',
  brandVideo: '',
  media: [],
  industry: {},
  backgroundColor: '',
  collapsedDescription: '',
  totalReviews: 0,
  assetPageSlug: '',
  topBlock: {
    current: null
  }
}

export default TopBlock
